import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type {
  FeedbackProposal,
  FeedbackReview,
  PhaseView,
  ProposalKind,
  ProposalTarget,
} from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * What the agent proposes to do with the feedback it has been given, and the
 * gate an operator decides at.
 *
 * <p>Captured feedback accumulates but does not consolidate: the same lesson
 * recorded five times stays five entries competing for three retrieval slots.
 * The analyst reads the corpus and proposes where each lesson actually belongs
 * — usually one line in a phase's rules, which applies to every message in that
 * phase instead of only when a thread happens to look similar.
 *
 * <p>Nothing here writes anything until Approve is pressed, and the text is
 * editable first: the point of the gate is that a human decides the exact
 * wording that will govern every future message.
 */

const KIND_LABEL: Record<ProposalKind, string> = {
  NEW_RULE: "New rule",
  MERGE: "Replaces a rule",
  KEEP_AS_EXAMPLE: "Keep as example",
  NO_ACTION: "Already covered",
};

const KIND_TONE: Record<ProposalKind, string> = {
  NEW_RULE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  MERGE: "bg-sky-50 text-sky-700 border-sky-200",
  KEEP_AS_EXAMPLE: "bg-zinc-50 text-zinc-600 border-zinc-200",
  NO_ACTION: "bg-amber-50 text-amber-800 border-amber-200",
};

const TARGET_LABEL: Record<ProposalTarget, string> = {
  AI_RULE: "phase rules",
  MANUAL_PROMPT: "manual prompt",
  FEEDBACK_RULE: "strategy-wide note",
  NONE: "no change",
};

export default function FeedbackProposalsCard({
  strategyId,
  phases,
  canDecide,
}: {
  strategyId: string;
  phases: PhaseView[];
  /** Approving rewrites the strategy config, so it is ADMIN-only. */
  canDecide: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const reviewsQ = useQuery({
    queryKey: ["feedback-reviews", strategyId],
    queryFn: () => api.get<FeedbackReview[]>(`/api/v1/strategies/${strategyId}/feedback/reviews`),
    // A queued analysis finishes out of band; poll while one is in flight.
    refetchInterval: (query) => {
      const data = query.state.data as FeedbackReview[] | undefined;
      return data?.some((r) => r.status === "PENDING" || r.status === "RUNNING") ? 3000 : false;
    },
  });

  const analyze = useMutation({
    mutationFn: () => api.post<FeedbackReview>(`/api/v1/strategies/${strategyId}/feedback/reviews`),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["feedback-reviews", strategyId] });
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not start the analysis."),
  });

  const reviews = reviewsQ.data ?? [];
  const running = reviews.some((r) => r.status === "PENDING" || r.status === "RUNNING");
  const pending = reviews.flatMap((r) => r.proposals.filter((p) => p.status === "PENDING"));
  const latestSweep = reviews.find((r) => r.trigger === "MANUAL" && r.status === "READY");
  const failed = reviews.find((r) => r.status === "FAILED");

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div>
          <CardTitle>Proposed improvements</CardTitle>
          <p className="text-sm text-[var(--fg-muted)]">
            What the agent suggests doing with the feedback it has been given. A recurring lesson
            becomes one line in a phase's rules, which applies to every message rather than only
            when a thread looks similar. Nothing is written until you approve it.
          </p>
        </div>
        <button
          type="button"
          onClick={() => analyze.mutate()}
          disabled={running || analyze.isPending}
          className="shrink-0 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm
                     hover:bg-zinc-50 disabled:opacity-50"
        >
          {running ? "Analysing…" : "Analyse all feedback"}
        </button>
      </div>

      {error ? <p className="mt-3 text-sm text-[var(--danger)]">{error}</p> : null}

      {failed && !running ? (
        <p className="mt-3 text-sm text-[var(--danger)]">
          The last analysis failed: {failed.error ?? "unknown reason"}
        </p>
      ) : null}

      {latestSweep?.summary ? (
        <section className="mt-4 rounded-md border border-[var(--border)] bg-zinc-50/60 p-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-[var(--fg-muted)]">
            What the feedback shows
          </h3>
          <p className="mt-1 whitespace-pre-wrap text-sm">{latestSweep.summary}</p>
          <p className="mt-2 text-xs text-[var(--fg-muted)]">
            {latestSweep.entriesConsidered} entries reviewed ·{" "}
            {formatDateTime(latestSweep.updatedAt)}
          </p>
        </section>
      ) : null}

      <div className="mt-4 space-y-3">
        {reviewsQ.isLoading ? (
          <p className="text-sm text-zinc-500">Loading…</p>
        ) : pending.length === 0 ? (
          <p className="text-sm text-[var(--fg-muted)]">
            {running
              ? "Reading the feedback…"
              : "No proposals waiting. New feedback is analysed as you add it."}
          </p>
        ) : (
          pending.map((p) => (
            <ProposalCard
              key={p.id}
              strategyId={strategyId}
              proposal={p}
              phases={phases}
              canDecide={canDecide}
            />
          ))
        )}
      </div>

      <AppliedTrail reviews={reviews} phases={phases} />
    </Card>
  );
}

function ProposalCard({
  strategyId,
  proposal,
  phases,
  canDecide,
}: {
  strategyId: string;
  proposal: FeedbackProposal;
  phases: PhaseView[];
  canDecide: boolean;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState(proposal.proposedText);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["feedback-reviews", strategyId] });
    qc.invalidateQueries({ queryKey: ["feedback", strategyId] });
    qc.invalidateQueries({ queryKey: ["strategy-detail"] });
  };

  const decide = (action: "approve" | "reject") =>
    api
      .post<FeedbackProposal>(
        `/api/v1/strategies/${strategyId}/feedback/proposals/${proposal.id}/${action}`,
        action === "approve" ? { text } : {},
      )
      .then(invalidate);

  const approve = useMutation({
    mutationFn: () => decide("approve"),
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not apply this proposal."),
  });
  const reject = useMutation({
    mutationFn: () => decide("reject"),
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not reject this proposal."),
  });

  const writesNothing = proposal.target === "NONE";

  return (
    <article className="rounded-md border border-[var(--border)] p-3">
      <header className="flex flex-wrap items-center gap-2">
        <span className={cn("rounded border px-1.5 py-0.5 text-xs", KIND_TONE[proposal.kind])}>
          {KIND_LABEL[proposal.kind]}
        </span>
        <span className="text-xs text-[var(--fg-muted)]">
          → {TARGET_LABEL[proposal.target]}
          {proposal.targetPhaseId ? ` · ${phaseLabel(phases, proposal.targetPhaseId)}` : ""}
          {proposal.kind === "MERGE" && proposal.targetRuleIndex !== null
            ? ` · replaces rule ${proposal.targetRuleIndex}`
            : ""}
        </span>
        <span className="ml-auto text-xs text-[var(--fg-muted)]">
          from {proposal.sourceFeedbackIds.length}{" "}
          {proposal.sourceFeedbackIds.length === 1 ? "entry" : "entries"}
        </span>
      </header>

      {writesNothing ? (
        <p className="mt-2 text-sm">{proposal.proposedText}</p>
      ) : (
        <label className="mt-2 block">
          <span className="text-xs text-[var(--fg-muted)]">
            The exact line that will be added — edit it before approving if the wording is not
            right
          </span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            disabled={!canDecide}
            aria-label="Proposed rule"
            className="mt-1 w-full rounded-md border border-[var(--border)] px-2 py-1.5 text-sm
                       disabled:bg-zinc-50"
          />
        </label>
      )}

      <p className="mt-1 text-sm text-[var(--fg-muted)]">{proposal.rationale}</p>

      {error ? <p className="mt-2 text-sm text-[var(--danger)]">{error}</p> : null}

      {canDecide ? (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => approve.mutate()}
            disabled={approve.isPending || reject.isPending || (!writesNothing && !text.trim())}
            className="rounded-md bg-zinc-900 px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {writesNothing ? "Accept" : "Approve and add"}
          </button>
          <button
            type="button"
            onClick={() => reject.mutate()}
            disabled={approve.isPending || reject.isPending}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm
                       hover:bg-zinc-50 disabled:opacity-50"
          >
            Reject
          </button>
        </div>
      ) : (
        <p className="mt-3 text-xs text-[var(--fg-muted)]">
          Only an admin can apply a proposal — it changes the strategy configuration.
        </p>
      )}
    </article>
  );
}

/**
 * What has already been added, and where. This is the answer to "what did it
 * change?" — and, months later, to "why does this rule exist?".
 */
function AppliedTrail({
  reviews,
  phases,
}: {
  reviews: FeedbackReview[];
  phases: PhaseView[];
}) {
  const applied = reviews
    .flatMap((r) => r.proposals)
    .filter((p) => p.status === "APPLIED")
    .slice(0, 10);
  if (applied.length === 0) return null;

  return (
    <section className="mt-5 border-t border-[var(--border)] pt-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-[var(--fg-muted)]">
        Added from feedback
      </h3>
      <ul className="mt-2 space-y-2">
        {applied.map((p) => (
          <li key={p.id} className="text-sm">
            <span className="text-[var(--fg-muted)]">
              {TARGET_LABEL[p.target]}
              {p.targetPhaseId ? ` · ${phaseLabel(phases, p.targetPhaseId)}` : ""} ·{" "}
              {p.decidedAt ? formatDateTime(p.decidedAt) : ""}
            </span>
            <div>{p.appliedText ?? p.proposedText}</div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function phaseLabel(phases: PhaseView[], phaseId: string): string {
  return phases.find((p) => p.id === phaseId)?.name ?? "unknown phase";
}
